import type { CountrySlug } from "./countries";

export interface HighlightItem {
  title: string;
  description: string;
  meta?: string;
}

export interface CountryHighlights {
  destinations: HighlightItem[];
  experiences: HighlightItem[];
  events: HighlightItem[];
  /** approximate x/y percent on the world map (0-100) */
  mapX: number;
  mapY: number;
  /** high-quality showcase image */
  image: string;
}

export const HIGHLIGHTS: Record<CountrySlug, CountryHighlights> = {
  usa: {
    mapX: 22,
    mapY: 40,
    image:
      "/img/u/photo-1485871981521-5b1fd3805eee.webp",
    destinations: [
      { title: "New York City", description: "The financial and cultural capital of the East Coast.", meta: "Northeast" },
      { title: "San Francisco Bay", description: "Tech hub, Golden Gate, and Silicon Valley access.", meta: "California" },
      { title: "Miami", description: "Beaches, Latin culture, and a booming remote-work scene.", meta: "Florida" },
    ],
    experiences: [
      { title: "Grand Canyon Road Trip", description: "Route 66 from Vegas to the South Rim." },
      { title: "Broadway Night", description: "Catch a Tony-winning show in Manhattan." },
      { title: "Napa Valley Wine Tour", description: "World-class vineyards an hour from SF." },
    ],
    events: [
      { title: "SXSW Austin", description: "Music, film, and tech festival.", meta: "March" },
      { title: "US Open Tennis", description: "Grand Slam action in Queens, NYC.", meta: "August" },
      { title: "Coachella", description: "Iconic desert music festival.", meta: "April" },
    ],
  },
  uk: {
    mapX: 47,
    mapY: 30,
    image:
      "/img/u/photo-1513635269975-59663e0ac1ad.webp",
    destinations: [
      { title: "London", description: "Global finance, culture, and endless neighborhoods.", meta: "England" },
      { title: "Edinburgh", description: "Historic capital of Scotland with a stunning skyline.", meta: "Scotland" },
      { title: "The Cotswolds", description: "Storybook villages and rolling countryside." , meta: "SW England"},
    ],
    experiences: [
      { title: "West End Theatre", description: "Premier productions in London's theatre district." },
      { title: "Scottish Highlands Tour", description: "Lochs, castles, and dramatic landscapes." },
      { title: "Traditional Afternoon Tea", description: "A quintessential British ritual." },
    ],
    events: [
      { title: "Wimbledon", description: "The world's oldest tennis championship.", meta: "July" },
      { title: "Edinburgh Fringe", description: "The largest arts festival on earth.", meta: "August" },
      { title: "Notting Hill Carnival", description: "Europe's biggest street festival.", meta: "August" },
    ],
  },
  canada: {
    mapX: 20,
    mapY: 28,
    image:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/b/bc/Lake_Agnes_im_Banff_National_Park.jpg/1280px-Lake_Agnes_im_Banff_National_Park.jpg",
    destinations: [
      { title: "Banff National Park", description: "Turquoise lakes and Rocky Mountain peaks.", meta: "Alberta" },
      { title: "Toronto", description: "Canada's largest, most diverse metropolis.", meta: "Ontario" },
      { title: "Vancouver", description: "Ocean, mountains, and rainforest in one city.", meta: "BC" },
    ],
    experiences: [
      { title: "Northern Lights, Yukon", description: "Aurora chasing in one of the best viewing spots on earth." },
      { title: "Niagara Falls", description: "See the falls from the Canadian side." },
      { title: "Whistler Skiing", description: "North America's largest ski resort." },
    ],
    events: [
      { title: "Toronto Film Festival", description: "TIFF — a major stop on the awards circuit.", meta: "September" },
      { title: "Calgary Stampede", description: "The greatest outdoor show on earth.", meta: "July" },
      { title: "Winterlude Ottawa", description: "Skating on the Rideau Canal.", meta: "February" },
    ],
  },
  australia: {
    mapX: 82,
    mapY: 75,
    image:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/c/cd/Hobart-Tasmania-Australia12.JPG/1280px-Hobart-Tasmania-Australia12.JPG",
    destinations: [
      { title: "Sydney", description: "Iconic Opera House, harbour, and beaches.", meta: "NSW" },
      { title: "Melbourne", description: "Coffee, laneways, and cultural capital vibe.", meta: "Victoria" },
      { title: "Great Barrier Reef", description: "The world's largest coral reef system.", meta: "Queensland" },
    ],
    experiences: [
      { title: "Uluru Sunrise", description: "The spiritual heart of the outback." },
      { title: "Great Ocean Road", description: "One of the world's most scenic coastal drives." },
      { title: "Bondi to Coogee Walk", description: "6km of Sydney's best coastal cliffs." },
    ],
    events: [
      { title: "Australian Open", description: "The first Grand Slam of the tennis year.", meta: "January" },
      { title: "Vivid Sydney", description: "Light, music, and ideas festival.", meta: "May–June" },
      { title: "Melbourne Cup", description: "The race that stops a nation.", meta: "November" },
    ],
  },
  italy: {
    mapX: 52,
    mapY: 37,
    image:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/5/53/Colosseum_in_Rome%2C_Italy_-_April_2007.jpg/1280px-Colosseum_in_Rome%2C_Italy_-_April_2007.jpg",
    destinations: [
      { title: "Rome", description: "The eternal city — 3,000 years of history.", meta: "Lazio" },
      { title: "Florence", description: "Renaissance art, architecture, and Tuscan food.", meta: "Tuscany" },
      { title: "Amalfi Coast", description: "Cliff-side villages above turquoise sea.", meta: "Campania" },
    ],
    experiences: [
      { title: "Gondola Ride in Venice", description: "Glide through the canals at sunset." },
      { title: "Tuscan Wine Country", description: "Chianti tastings and rolling vineyards." },
      { title: "Pompeii Ruins Tour", description: "A city frozen in time by Vesuvius." },
    ],
    events: [
      { title: "Venice Film Festival", description: "The world's oldest film festival.", meta: "August" },
      { title: "Carnevale di Venezia", description: "Masked balls and canal parades.", meta: "February" },
      { title: "Palio di Siena", description: "The historic bareback horse race.", meta: "July / August" },
    ],
  },
  portugal: {
    mapX: 45,
    mapY: 38,
    image:
      "/img/u/photo-1555881400-74d7acaacd8b.webp",
    destinations: [
      { title: "Lisbon", description: "Hillside tiles, trams, and Atlantic light.", meta: "Capital" },
      { title: "Porto", description: "Port wine cellars along the Douro river.", meta: "North" },
      { title: "The Algarve", description: "Golden cliffs and hidden sea caves.", meta: "South Coast" },
    ],
    experiences: [
      { title: "Fado Night in Alfama", description: "Portugal's soulful national music." },
      { title: "Douro Valley Cruise", description: "Terraced vineyards from a river boat." },
      { title: "Surf in Ericeira", description: "Europe's only World Surfing Reserve." },
    ],
    events: [
      { title: "Web Summit Lisbon", description: "Europe's largest tech conference.", meta: "November" },
      { title: "Festas de Lisboa", description: "Sardines, parades, and pop-up bars.", meta: "June" },
      { title: "NOS Alive", description: "One of Europe's top music festivals.", meta: "July" },
    ],
  },
};
